// /**
//  * parcel - Edit
//  */

// React
import React, {useContext, useEffect} from 'react';

// react hook form
import {useForm} from 'react-hook-form';

// UI
import {makeStyles, Theme, createStyles} from '@material-ui/core/styles';
import {
  TextField,
  Grid,
  Button,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import throttle from 'lodash/throttle';

// Icon
import Icon from '@mdi/react';
import {
  mdiCounter,
  mdiFormatListCheckbox,
  mdiArchiveOutline,
  mdiKey,
  mdiFormatListBulletedType,
  mdiPackage,
  mdiNumeric,
} from '@mdi/js';

// Client SDK
import Client from 'client-sdk/src/client';
import {
  GetAllProductsRequestBuilder,
  GetPartnerCategoriesRequestBuilder,
  GetVariantsRequestBuilder,
} from 'client-sdk/src/products/products';

// Type
import {AddItemCreateParcel} from '../../../parcels/interfaces';
import {
  CategoryObject,
  Variant,
  VariantFeature,
} from '../../../products/interfaces';
import {Product} from '../../../products/interfaces';

// helpers'
import {flattenObject, removeEmptyValueObject} from '../../../../utils/helpers';
import AuthContext from '../../../../authContext';
import List from './variantFeaturesList';
import {useDebounce} from 'use-lodash-debounce';

type Props = {
  addItem: (body: AddItemCreateParcel) => void;

  onClose: () => void;
};

type Inputs = {
  count: number;
  product: string;
  categoryName: string;
  productCode: number;
  variant: string;
  unitCost: string;
  variantCode: number;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    margin: {
      // margin: theme.spacing(1),
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textField: {
      // width: '25ch',
    },
    textInput: {
      // width: '25ch',
      '& input, .MuiSelect-root': {
        padding: '6px 0 12px !important',
      },

      '& .MuiInput-underline::before': {
        borderBottom: '1px solid #d8d8d8 !important',
      },
    },
    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontWeight: 500,
      width: '100%',
      '& > *': {
        // margin: theme.spacing(0, 0, 0, 1),
      },
    },
    icon: {
      // marginRight: theme.spacing(1),
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8A8A8A',
      marginRight: '8px',
      marginTop: '21px',
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })
);

const token = localStorage.getItem('token') || '';

export default function Add(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const {
    register,
    handleSubmit,
    errors,
    watch,
    getValues,
    setValue,
    reset,
  } = useForm<Inputs>();

  const [valueVariant, setValueVariant] = React.useState<Variant | null>(null);
  const [inputValueVariant, setInputValueVariant] = React.useState('');
  const [hasExpirationDate, setHasExpirationDate] = React.useState(false);
  const [optionsVariants, setOptionsVariant] = React.useState<Variant[]>([]);
  const [optionsVariantFeatures, setOptionsVariantFeatures] = React.useState<
    VariantFeature[]
  >([]);
  const loadingVariants = React.useRef(false);

  const [valueProduct, setValueProduct] = React.useState<Product | null>(null);
  const [inputValueProduct, setInputValueProduct] = React.useState('');
  const [optionsProduct, setOptionsProduct] = React.useState<Product[]>([]);

  const [
    valueCategory,
    setValueCategory,
  ] = React.useState<CategoryObject | null>(null);
  const [inputValueCategory, setInputValueCategory] = React.useState('');
  const [optionsCategory, setOptionsCategory] = React.useState<
    CategoryObject[]
  >([]);
  const debouncedValue = useDebounce(getValues().productCode, 1000);

  const loadingProduct = React.useRef(false);

  useEffect(() => {
    if (!valueCategory) {
      setValueProduct(null);
      setValueVariant(null);
    }
  }, [valueCategory]);

  const handleCountChange = () => {
    const values = getValues();
    setValue('count', values.countOfBox * values.boxCount);
  };

  if (typeof window !== 'undefined' && !loadingVariants.current) {
    loadingVariants.current = true;
  }

  if (typeof window !== 'undefined' && !loadingVariants.current) {
    loadingProduct.current = true;
  }

  const fetchVariants = React.useMemo(
    () =>
      throttle(
        (
          request: {input: string; product: object},
          callback: (results?: Variant[]) => void
        ) => {
          (async () => {
            if (valueVariant?.id) {
              const makeRequest = new GetVariantsRequestBuilder()
                .id(valueVariant?.id)
                .params({
                  size: 1000,
                })
                .build();
              const client = new Client(token);
              const response = await client.execute(makeRequest);
              const variants = response.data.content as any;
              callback(variants);
            }
          })();
        },
        200
      ),
    []
  );

  const fetchProducts = React.useMemo(
    () =>
      throttle(
        (
          request: {input: string; category: object; filter: object},
          callback: (results?: Product[]) => void
        ) => {
          const {input, category, filter} = request;
          (async () => {
            let params: any = {
              'name.contains': input,
              'partnerId.equals': authContext?.user?.partnerId,
              size: 10000,
              ...filter,
            };
            if (category) {
              params = {
                'name.contains': input,
                'partnerId.equals': authContext?.user?.partnerId,
                'categoryId.specified': false,
                size: 10000,
                ...filter,
              };
              if (category.id) {
                params = {
                  'name.contains': input,
                  'partnerId.equals': authContext?.user?.partnerId,
                  'categoryId.equals': category?.id,
                  size: 10000,
                  ...filter,
                };
              }
            }
            const makeRequest = new GetAllProductsRequestBuilder()
              .params(params)
              .build();
            const client = new Client(token);
            const response = await client.execute(makeRequest);
            const products = response.data as any;
            callback(products);
          })();
        },
        200
      ),
    []
  );

  React.useEffect(() => {
    let active = true;
    const partnerId = authContext?.user?.partnerId;
    if (inputValueProduct === '' && partnerId && valueCategory) {
      setOptionsProduct(valueProduct ? [valueProduct] : []);

      (async () => {
        const params = valueCategory.id
          ? {
              size: 1000,
              'partnerId.equals': authContext?.user?.partnerId || null,
              'categoryId.equals': valueCategory.id || null,
            }
          : {
              size: 1000,
              'partnerId.equals': authContext?.user?.partnerId || null,
              'categoryId.specified': 'false',
            };

        const makeRequest = new GetAllProductsRequestBuilder()
          .params(params)
          .build();
        const client = new Client(token);
        const response = await client.execute(makeRequest);
        const product = response.data as any;
        if (active) {
          setOptionsProduct(product as Product[]);
        }
      })();

      return undefined;
    }

    if (partnerId) {
      fetchProducts(
        {input: inputValueProduct, category: valueCategory},
        (results?: Product[]) => {
          if (active) {
            let newOptions = [] as Product[];

            if (valueProduct) {
              newOptions = [valueProduct];
            }

            if (results) {
              newOptions = [...newOptions, ...results];
            }

            setOptionsProduct(newOptions);
          }
        }
      );
    } else if (!valueCategory) {
      setOptionsProduct([]);
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      setValueProduct('');
    }

    return () => {
      active = false;
    };
  }, [
    valueCategory,
    inputValueCategory,
    valueProduct,
    inputValueProduct,
    fetchProducts,
  ]);

  React.useEffect(() => {
    let active = true;
    const partnerId = authContext?.user?.partnerId;
    if (partnerId && valueVariant) {
      setOptionsProduct(valueProduct ? [valueProduct] : []);

      (async () => {
        const makeRequest = new GetVariantsRequestBuilder()
          .id(valueVariant?.id)
          .params({
            size: 1000,
            'productId.equals': valueProduct?.id,
            'partnerId.equals': authContext?.user?.partnerId || null,
          })
          .build();
        const client = new Client(token);
        const response = await client.execute(makeRequest);
        const features = response.data.features as any;
        if (active) {
          setOptionsVariantFeatures(features as VariantFeature[]);
        }
      })();

      return undefined;
    }

    if (!valueVariant) {
      setOptionsVariantFeatures([]);
    }

    return () => {
      active = false;
    };
  }, [inputValueVariant, valueVariant]);

  const fetchCategories = React.useMemo(
    () =>
      throttle(
        (
          request: {input: string},
          callback: (results?: CategoryObject[]) => void
        ) => {
          const {input} = request;
          (async () => {
            const makeRequest = new GetPartnerCategoriesRequestBuilder()
              .params({
                'name.contains': input,
                'partnerId.equals': authContext?.user?.partnerId,
                size: input ? 10000 : 1000,
              })
              .build();
            const client = new Client(token);
            const response = await client.execute(makeRequest);
            const products = response.data as any;
            callback(products);
          })();
        },
        200
      ),
    []
  );

  React.useEffect(() => {
    let active = true;
    const partnerId = authContext?.user?.partnerId;
    if (inputValueCategory === '' && partnerId) {
      setOptionsCategory(valueCategory ? [valueCategory] : []);

      (async () => {
        const makeRequest = new GetPartnerCategoriesRequestBuilder()
          .params({
            size: 100,
            'partnerId.equals': authContext?.user?.partnerId || null,
          })
          .build();
        const client = new Client(token);
        const response = await client.execute(makeRequest);
        const category = response.data as any;
        const categories = [
          ...category,
          {id: 0, name: 'بدون دسته بندی', code: 0},
        ];
        if (active) {
          setOptionsCategory(categories as CategoryObject[]);
        }
      })();

      return undefined;
    }

    if (partnerId) {
      fetchCategories(
        {input: inputValueVariant},
        (results?: CategoryObject[]) => {
          if (active) {
            let newOptions = [] as CategoryObject[];

            if (valueCategory) {
              newOptions = [valueCategory];
            }

            if (results) {
              newOptions = [...newOptions, ...results];
            }

            setOptionsCategory(newOptions);
          }
        }
      );
    }

    return () => {
      active = false;
    };
  }, [valueCategory, inputValueCategory]);
  // fetchCategories

  React.useEffect(() => {
    let active = true;

    if (inputValueVariant === '' && valueProduct?.id) {
      setOptionsVariant(valueVariant ? [valueVariant] : []);

      (async () => {
        const makeRequest = new GetVariantsRequestBuilder()
          .params({
            'productId.equals': valueProduct?.id,
            size: 1000,
          })
          .build();
        const client = new Client(token);
        const response = await client.execute(makeRequest);
        const variant = response.data.content as any;
        if (active) {
          setOptionsVariant(variant as Variant[]);
        }
      })();
      return undefined;
    }

    if (valueProduct?.id) {
      fetchVariants(
        {input: inputValueVariant, product: valueProduct},
        (results?: Variant[]) => {
          if (active) {
            let newOptions = [] as Variant[];

            if (valueVariant) {
              newOptions = [valueVariant];
            }

            if (results) {
              newOptions = [...newOptions, ...results];
            }

            setOptionsVariant(newOptions);
          }
        }
      );
    } else if (!valueProduct) {
      setOptionsVariant([]);
      setValueVariant('');
    }

    return () => {
      active = false;
    };
  }, [
    valueProduct,
    inputValueProduct,
    valueVariant,
    inputValueVariant,
    fetchVariants,
  ]);

  const onSubmit = (data: any) => {
    const {
      count,
      countOfBox,
      boxCount,
      productCode,
      variantCode,
      unitCost,
    } = data;
    let variantObject = undefined;
    let variantCodeStr = undefined;
    if (optionsVariants.length) {
      variantObject = optionsVariants[0];
      variantCodeStr = optionsVariants[0]?.code;
      if (optionsVariants.length > 1) {
        variantObject = valueVariant;
        variantCodeStr = variantCode;
      }
    }
    props.addItem({
      count,
      boxCount,
      productCode,
      countOfBox,
      variantCode: variantCodeStr,
      variant: variantObject,
      product: valueProduct,
      category: valueCategory,
      unitCost,
      hasExpirationDate,
    });
  };

  useEffect(() => {
    let active = true;
    if (getValues()?.productCode && valueProduct?.code !== getValues()?.productCode) {
      const params = getValues()?.productCode
        ? {
          input: inputValueProduct,
          category: valueCategory,
          filter: {'code.equals': getValues()?.productCode},
        }
        : {input: inputValueProduct, category: valueCategory};
      fetchProducts(params,
        (results?: Product[]) => {
          if (active) {
            let newOptions = [] as Product[];

            if (valueProduct) {
              newOptions = [valueProduct];
            }

            if (results) {
              newOptions = [...newOptions, ...results];
            }

            if (results?.length === 1) setValueProduct(results[0]);
            if (results?.length === 0) setValueProduct({} as Product);

            setOptionsProduct(newOptions);
          }
        }
      );
    }
    return () => {
      active = false;
    };
  }, [debouncedValue]);

  useEffect(() => {
    if (valueProduct?.code) {
      setValue('productCode', valueProduct.code);
    }
  }, [valueProduct]);


  const flattVariants = (
    productName: string,
    item: any,
    sellerName: string,
    providerName: string
  ) => {
    const descriptionArray: string[] = [productName];

    if (sellerName) {
      descriptionArray.push(`فروشنده: ${sellerName}`);
    }

    if (providerName) {
      descriptionArray.push(`تامین کننده: ${providerName}`);
    }

    item.forEach((el: {key: string; value: string}) => {
      descriptionArray.push(`${el.key}: ${el.value} `);
    });
    return {
      shortName: descriptionArray.join(',').substring(0, 40) + '...',
      fullName: descriptionArray.join(','),
    };
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={4} alignItems="flex-start">
          <Grid container item xs={12} md={6} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiFormatListCheckbox}
                  size={1}
                  className={classes.icon}
                  style={{transform: 'rotate(-180deg)'}}
                />
              </span>

              <span style={{width: '100%'}}>
                <FormControl style={{width: '100%'}}>
                  <Autocomplete
                    id="category"
                    getOptionLabel={option =>
                      typeof option === 'string' ? option : option.name
                    }
                    filterOptions={x => x}
                    options={optionsCategory}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    noOptionsText="دسته‌بندی موجود نیست"
                    disabled={!optionsCategory.length}
                    className={classes.textInput}
                    value={valueCategory}
                    onChange={(event: any, newValue: CategoryObject | null) => {
                      setOptionsCategory(
                        newValue
                          ? [newValue, ...optionsCategory]
                          : optionsCategory
                      );
                      setValueCategory(newValue);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setInputValueCategory(newInputValue);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        name="category"
                        label="دسته بندی"
                        variant="standard"
                        fullWidth
                      />
                    )}
                    renderOption={option => {
                      return (
                        <Grid container alignItems="center">
                          <Grid item xs>
                            {option.name}
                            <Typography variant="body2" color="textSecondary">
                              {/* {option.address}--{option.phoneNumber} */}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    }}
                  />
                </FormControl>
              </span>
            </div>
          </Grid>
          <Grid container item xs={12} md={6} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiArchiveOutline}
                  size={1}
                  className={classes.icon}
                />
              </span>

              <span style={{width: '100%'}}>
                <FormControl style={{width: '100%'}}>
                  <Autocomplete
                    id="products"
                    getOptionLabel={option =>
                      typeof option === 'string' ? option : option.name
                    }
                    filterOptions={x => x}
                    options={optionsProduct}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    noOptionsText="کالایی وجود ندارد"
                    className={classes.textInput}
                    value={valueProduct}
                    onChange={(event: any, newValue: Product | null) => {
                      setOptionsProduct(
                        newValue
                          ? [newValue, ...optionsProduct]
                          : optionsProduct
                      );
                      setValueProduct(newValue);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setInputValueProduct(newInputValue);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="نام کالا"
                        name="product"
                        variant="standard"
                        fullWidth
                      />
                    )}
                    renderOption={option => {
                      return (
                        <Grid container alignItems="center">
                          <Grid item xs>
                            {option.name}
                            <Typography variant="body2" color="textSecondary">
                              {/* {option.address}--{option.phoneNumber} */}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    }}
                  />
                </FormControl>
              </span>
            </div>
          </Grid>
          <Grid container item xs={12} md={6} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiKey} size={1} className={classes.icon} />
              </span>

              <div style={{width: '100%'}}>
                <TextField
                  fullWidth
                  id="productCode"
                  name="productCode"
                  className={classes.textInput}
                  InputLabelProps={{
                    shrink: !!valueProduct || !!getValues().productCode,
                  }}
                  label="کد کالا"
                  inputRef={register({required: true})}
                  error={errors.count && true}
                />
              </div>
            </div>
          </Grid>
          <Grid container item xs={12} md={6} spacing={1} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiFormatListBulletedType}
                  size={1}
                  className={classes.icon}
                />
              </span>

              <div style={{width: '100%'}}>
                <FormControl style={{width: '100%'}}>
                  <Autocomplete
                    id="variant"
                    getOptionLabel={option =>
                      typeof option === 'string'
                        ? option
                        : String(
                            flattVariants(
                              inputValueProduct,
                              option.features,
                              option.sellerName,
                              option.providerName
                            ).shortName
                          )
                    }
                    filterOptions={x => x}
                    options={optionsVariants}
                    autoComplete
                    includeInputInList
                    className={classes.textInput}
                    disabled={
                      !inputValueProduct || optionsVariants.length === 0
                    }
                    filterSelectedOptions
                    noOptionsText="تنوع کالایی وجود ندارد"
                    value={valueVariant}
                    onChange={(event: any, newValue: Variant | null) => {
                      setOptionsVariant(
                        newValue
                          ? [newValue, ...optionsVariants]
                          : optionsVariants
                      );
                      setValueVariant(newValue);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setInputValueVariant(newInputValue);
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        name="variant"
                        label="نام تنوع کالا"
                        variant="standard"
                        fullWidth
                      />
                    )}
                    renderOption={option => {
                      return (
                        <Grid container alignItems="center">
                          <Grid item xs>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              title={
                                flattVariants(
                                  inputValueProduct,
                                  option.features,
                                  option.sellerName,
                                  option.providerName
                                ).fullName
                              }
                            >
                              {
                                flattVariants(
                                  inputValueProduct,
                                  option.features,
                                  option.sellerName,
                                  option.providerName
                                ).shortName
                              }
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    }}
                  />
                </FormControl>
              </div>
            </div>
          </Grid>
          <Grid container item xs={12} md={6} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiKey} size={1} className={classes.icon} />
              </span>

              <div style={{width: '100%'}}>
                <TextField
                  fullWidth
                  id="variantCode"
                  name="variantCode"
                  className={classes.textInput}
                  value={valueVariant ? valueVariant?.code : ''}
                  disabled={optionsVariants.length === 0}
                  InputProps={{
                    readOnly: true,
                  }}
                  InputLabelProps={{
                    shrink: !!valueVariant,
                  }}
                  label="کد تنوع کالا"
                  inputRef={register({required: optionsVariants.length > 0})}
                  error={errors.count && true}
                />
              </div>
            </div>
          </Grid>
          <Grid container item xs={12} md={6} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCounter} size={1} className={classes.icon} />
              </span>

              <div style={{width: '100%'}}>
                <TextField
                  fullWidth
                  id="count"
                  name="count"
                  className={classes.textInput}
                  label="تعداد"
                  inputRef={register({required: true})}
                  error={errors.count && true}
                />
              </div>
            </div>
          </Grid>

          <Grid container item xs={12} md={6} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiNumeric} size={1} className={classes.icon} />
              </span>

              <div style={{width: '100%'}}>
                <TextField
                  fullWidth
                  id="unitCost"
                  name="unitCost"
                  className={classes.textInput}
                  label="قیمت"
                  inputRef={register({required: false})}
                  error={errors.unitCost && true}
                />
              </div>
            </div>
          </Grid>

          <Grid container item xs={12} alignItems="flex-end">
            <div className={classes.item}>
              <span className={classes.label}>
                <Checkbox
                  checked={hasExpirationDate}
                  onChange={e => setHasExpirationDate(e.target.checked)}
                  color="secondary"
                  inputProps={{
                    'aria-label': 'secondary checkbox',
                  }}
                />
              </span>

              <div style={{width: '100%', marginTop: '20px'}}>
                <span>نیاز به تاریخ تولید و تاریخ انقضا دارد</span>
              </div>
            </div>
          </Grid>
          <>
            {optionsVariantFeatures.length ? (
              <Grid container style={{marginTop: '20px', marginBottom: '10px'}}>
                <List modal={true} data={optionsVariantFeatures} />
              </Grid>
            ) : null}
          </>
          <Grid item container xs={8}>
            <Button
              fullWidth
              type="submit"
              size="large"
              data-cy="search"
              autoFocus
              variant="contained"
              color="secondary"
              disabled={
                optionsVariants.length < 2
                  ? Object.keys(
                      flattenObject(
                        removeEmptyValueObject({
                          ...watch(),
                          inputValueProduct: inputValueProduct,
                        })
                      )
                    ).length !== 4
                  : Object.keys(
                      flattenObject(
                        removeEmptyValueObject({
                          ...watch(),
                          inputValueVariant: inputValueVariant,
                          inputValueProduct: inputValueProduct,
                        })
                      )
                    ).length !== 6
              }
            >
              {'مرحله بعد'}
            </Button>
          </Grid>
          <Grid item container xs={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={props.onClose}
              size="large"
              data-cy="search"
            >
              {'انصراف'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
